import { useEffect, useState, type ReactNode } from "react";
import clsx from "clsx";
import { CheckCircle2, Clapperboard, Pause, Play, Search, Sparkles, UserRound, X } from "lucide-react";
import {
  VIDEO_COMPANION_CONTEXT,
  VIDEO_COMPANION_CONTINUE_RECOMMENDATIONS,
  VIDEO_COMPANION_P1_RECOMMENDATIONS,
  VIDEO_COMPANION_QUICKLOOK_RECOMMENDATIONS,
  getVideoCompanionVideo,
  type VideoCompanionDemoState,
  type VideoCompanionVideo,
  type VideoCompanionVideoId
} from "../../config/videoCompanionDemo";
import { assetUrl } from "../../utils/assetUrl";
import "./NightVideoCompanionLayer.css";

const PLAYBACK_TICK_MS = 180;
const PLAYBACK_INCREMENT = 1.05;
const QUICKLOOK_THINKING_MS = 1800;
const ACTOR_PANEL_MS = 9000;
const P1_CARD_ACCENTS = ["cyan", "violet", "amber"] as const;

interface NightVideoCompanionLayerProps {
  evaSrc: string;
  state: VideoCompanionDemoState;
  onActorFinished: () => void;
  onAskActor: () => void;
  onOpenQuicklook: () => void;
  onPlaybackEnded: () => void;
  onReset: () => void;
  onSelectVideo: (videoId: VideoCompanionVideoId, nextStage?: VideoCompanionDemoState["stage"]) => void;
}

export function NightVideoCompanionLayer({
  evaSrc,
  state,
  onActorFinished,
  onAskActor,
  onOpenQuicklook,
  onPlaybackEnded,
  onReset,
  onSelectVideo
}: NightVideoCompanionLayerProps) {
  const selectedVideo = getVideoCompanionVideo(state.selectedVideoId);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [quicklookReady, setQuicklookReady] = useState(false);
  const isPlaybackVisible = state.stage !== "p1_recommend";

  useEffect(() => {
    if (state.stage === "p1_recommend") {
      setProgress(0);
      setIsPaused(false);
    }

    if (state.stage === "p2_playing") {
      setProgress(6);
      setIsPaused(false);
    }
  }, [state.selectedVideoId, state.stage]);

  useEffect(() => {
    if (state.stage !== "p2_playing" || isPaused) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setProgress((current) => {
        const nextProgress = Math.min(100, current + PLAYBACK_INCREMENT);

        if (nextProgress >= 100) {
          window.clearInterval(intervalId);
          onPlaybackEnded();
        }

        return nextProgress;
      });
    }, PLAYBACK_TICK_MS);

    return () => window.clearInterval(intervalId);
  }, [isPaused, onPlaybackEnded, state.stage, state.selectedVideoId]);

  useEffect(() => {
    if (state.stage !== "p4_quicklook") {
      return undefined;
    }

    setQuicklookReady(false);
    const timeoutId = window.setTimeout(() => setQuicklookReady(true), QUICKLOOK_THINKING_MS);
    return () => window.clearTimeout(timeoutId);
  }, [state.stage]);

  useEffect(() => {
    if (state.stage !== "p5_actor") {
      return undefined;
    }

    const timeoutId = window.setTimeout(onActorFinished, ACTOR_PANEL_MS);
    return () => window.clearTimeout(timeoutId);
  }, [onActorFinished, state.stage]);

  return (
    <section
      className={clsx("night-video-companion", `night-video-companion--${state.stage}`)}
      aria-label="视频搭子演示"
    >
      {state.stage === "p1_recommend" ? (
        <RecommendationDeck onSelectVideo={(videoId) => onSelectVideo(videoId, "p2_playing")} />
      ) : null}

      {isPlaybackVisible ? (
        <PlaybackSurface
          evaSrc={evaSrc}
          isPaused={isPaused}
          progress={progress}
          quicklookReady={quicklookReady}
          selectedVideo={selectedVideo}
          stage={state.stage}
          onAskActor={onAskActor}
          onOpenQuicklook={onOpenQuicklook}
          onPlaybackEnded={onPlaybackEnded}
          onReset={onReset}
          onSelectVideo={onSelectVideo}
          onTogglePlayback={() => setIsPaused((current) => !current)}
        />
      ) : null}
    </section>
  );
}

function RecommendationDeck({ onSelectVideo }: { onSelectVideo: (videoId: VideoCompanionVideoId) => void }) {
  return (
    <article className="night-video-recommend-panel" aria-label="充电等待视频推荐">
      <span className="night-video-recommend-panel__surface" aria-hidden="true" />

      <div className="night-video-recommend-panel__header">
        <h2>{VIDEO_COMPANION_CONTEXT.chargingCopy}</h2>
        <p>识别到充电等待场景，EVA 已按剩余时间和近期偏好筛选内容。</p>
      </div>

      <div className="night-video-recommend-panel__status" aria-label="充电场景状态">
        <span className="night-video-recommend-panel__ai-mark">AI</span>
        <span>{VIDEO_COMPANION_CONTEXT.time} 充电等待 · 视频推荐已就绪</span>
      </div>

      <div className="night-video-recommend-panel__cards">
        {VIDEO_COMPANION_P1_RECOMMENDATIONS.map((recommendation, index) => {
          const video = getVideoCompanionVideo(recommendation.videoId);
          const accent = P1_CARD_ACCENTS[index % P1_CARD_ACCENTS.length];

          return (
            <button
              key={video.id}
              className={clsx("night-video-recommend-card", `night-video-recommend-card--${accent}`)}
              type="button"
              onClick={() => onSelectVideo(video.id)}
              aria-label={`播放${video.title}${video.subtitle}`}
            >
              <VideoArtwork compact video={video} />
              <span className="night-video-recommend-card__copy">
                <strong>{video.title}</strong>
                <span className="night-video-recommend-card__details">
                  <span>{video.subtitle}</span>
                  <span>{recommendation.note} · {recommendation.badge}</span>
                </span>
              </span>
              <span className="night-video-recommend-card__badge" aria-hidden="true">
                {video.durationLabel}
              </span>
            </button>
          );
        })}
      </div>
    </article>
  );
}

function PlaybackSurface({
  evaSrc,
  isPaused,
  progress,
  quicklookReady,
  selectedVideo,
  stage,
  onAskActor,
  onOpenQuicklook,
  onPlaybackEnded,
  onReset,
  onSelectVideo,
  onTogglePlayback
}: {
  evaSrc: string;
  isPaused: boolean;
  progress: number;
  quicklookReady: boolean;
  selectedVideo: VideoCompanionVideo;
  stage: VideoCompanionDemoState["stage"];
  onAskActor: () => void;
  onOpenQuicklook: () => void;
  onPlaybackEnded: () => void;
  onReset: () => void;
  onSelectVideo: (videoId: VideoCompanionVideoId, nextStage?: VideoCompanionDemoState["stage"]) => void;
  onTogglePlayback: () => void;
}) {
  return (
    <div className="night-video-companion__playback">
      <button
        className={clsx("night-video-eva-pill", stage !== "p2_playing" && "night-video-eva-pill--active")}
        type="button"
        onClick={() => onOpenQuicklook()}
        aria-label="打开 EVA 速看推荐"
      >
        <img src={evaSrc} alt="" draggable={false} />
        <span>
          <strong>EVA</strong>
          <small>视频搭子</small>
        </span>
      </button>

      <div className="night-video-player" aria-label={`${selectedVideo.title}播放中`}>
        <img className="night-video-player__image" src={assetUrl(selectedVideo.imageSrc)} alt="" draggable={false} />
        <div className="night-video-player__shade" aria-hidden="true" />
        <button
          className="night-video-player__close"
          type="button"
          onClick={() => onReset()}
          aria-label="关闭播放，返回推荐页"
        >
          <X size={30} strokeWidth={2.1} />
        </button>
        <div className="night-video-player__meta">
          <em>{selectedVideo.category}</em>
          <h2>{selectedVideo.title}</h2>
          <p>{selectedVideo.subtitle} · {selectedVideo.durationLabel}</p>
        </div>
        <div className="night-video-player__actions">
          <button
            className={clsx(stage === "p5_actor" && "night-video-player__action--active")}
            type="button"
            onClick={() => onAskActor()}
            aria-label={stage === "p5_actor" ? "已识别当前画面人物" : "识别当前画面人物"}
          >
            {stage === "p5_actor" ? <CheckCircle2 size={20} /> : <UserRound size={20} />}
            {stage === "p5_actor" ? "已识别" : "这人是谁"}
          </button>
          <button type="button" onClick={() => onPlaybackEnded()}>
            <Clapperboard size={20} />
            片尾
          </button>
          <button type="button" onClick={() => onReset()}>
            推荐页
          </button>
        </div>
        <button
          className="night-video-player__play-toggle"
          type="button"
          onClick={() => onTogglePlayback()}
          aria-label={isPaused ? "继续播放" : "暂停播放"}
        >
          {isPaused ? <Play size={34} fill="currentColor" /> : <Pause size={34} fill="currentColor" />}
        </button>
        <div className="night-video-player__progress" aria-label={`播放进度 ${Math.round(progress)}%`}>
          <span style={{ transform: `scaleX(${progress / 100})` }} />
        </div>
      </div>

      {stage === "p3_continue" ? (
        <ContinuePanel evaSrc={evaSrc} onBack={onReset} onSelectVideo={onSelectVideo} />
      ) : null}

      {stage === "p4_quicklook" ? (
        <QuicklookPanel ready={quicklookReady} onSelectVideo={onSelectVideo} />
      ) : null}

      {stage === "p5_actor" ? (
        <ActorPanel video={selectedVideo} />
      ) : null}
    </div>
  );
}

function ContinuePanel({
  evaSrc,
  onBack,
  onSelectVideo
}: {
  evaSrc: string;
  onBack: () => void;
  onSelectVideo: (videoId: VideoCompanionVideoId, nextStage?: VideoCompanionDemoState["stage"]) => void;
}) {
  return (
    <aside className="night-video-continue-drawer" aria-label="片尾续看推荐">
      <button
        className="night-video-continue-drawer__close"
        type="button"
        onClick={() => onBack()}
        aria-label="关闭续看推荐"
      >
        <X size={28} strokeWidth={2} />
      </button>
      <img className="night-video-continue-drawer__eva" src={evaSrc} alt="" draggable={false} />

      <div className="night-video-continue-drawer__body">
        <header className="night-video-continue-drawer__header">
          <h2>还有时间，要继续看点别的吗</h2>
          <p>为你推荐了相关内容</p>
        </header>

        <div className="night-video-continue-drawer__list">
        {VIDEO_COMPANION_CONTINUE_RECOMMENDATIONS.map((recommendation) => {
          const video = getVideoCompanionVideo(recommendation.videoId);
          return (
            <button
              key={video.id}
              className="night-video-continue-item"
              type="button"
              onClick={() => onSelectVideo(video.id, "p2_playing")}
            >
              <VideoArtwork compact video={video} />
              <span className="night-video-continue-item__copy">
                <small>{recommendation.badge} · {video.category}</small>
                <strong>{video.title}</strong>
              </span>
              <em className="night-video-continue-item__badge">{recommendation.linkLabel}</em>
            </button>
          );
        })}
        </div>
      </div>
    </aside>
  );
}

function QuicklookPanel({
  ready,
  onSelectVideo
}: {
  ready: boolean;
  onSelectVideo: (videoId: VideoCompanionVideoId, nextStage?: VideoCompanionDemoState["stage"]) => void;
}) {
  return (
    <aside className="night-video-side-panel" aria-label="速看推荐">
      <PanelHeader
        icon={ready ? <Sparkles size={22} /> : <Search size={22} />}
        title={ready ? "刚出炉的速看版" : "正在帮你找"}
        subtitle={ready ? "帮你快速了解，想看哪个？" : "EVA 正在匹配短时长视频"}
      />
      {ready ? (
        <div className="night-video-side-panel__list night-video-side-panel__list--compact">
          {VIDEO_COMPANION_QUICKLOOK_RECOMMENDATIONS.map((recommendation) => {
            const video = getVideoCompanionVideo(recommendation.videoId);
            return (
              <button
                key={video.id}
                className="night-video-side-item night-video-side-item--quick"
                type="button"
                onClick={() => onSelectVideo(video.id, "p2_playing")}
              >
                <VideoArtwork compact video={video} />
                <span>
                  <strong>{video.title}</strong>
                  <small>{video.durationLabel} · {recommendation.note}</small>
                </span>
                <em>{recommendation.badge}</em>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="night-video-thinking" aria-label="检索中">
          <span />
          <span />
          <span />
        </div>
      )}
    </aside>
  );
}

function ActorPanel({ video }: { video: VideoCompanionVideo }) {
  const person = video.person;
  const personImageSrc = person.imageSrc ?? video.imageSrc;

  return (
    <aside className="night-video-side-panel night-video-side-panel--actor" aria-label="演员信息">
      <img className="night-video-side-panel__frame" src={assetUrl(personImageSrc)} alt={person.name} draggable={false} />
      <div className="night-video-actor-status" aria-label="识别结果">
        <CheckCircle2 size={18} />
        <span>已识别当前画面人物</span>
      </div>
      <PanelHeader
        icon={<UserRound size={22} />}
        title={person.name}
        subtitle={`${person.roleLabel} · ${person.relation}`}
      />
      <p>{person.summary}</p>
      <ul className="night-video-side-panel__facts">
        {person.facts.map((fact) => (
          <li key={fact}>{fact}</li>
        ))}
      </ul>
      <small className="night-video-side-panel__source">
        {person.sourceLabel}
        {person.imageCredit ? <span>{person.imageCredit}</span> : null}
      </small>
    </aside>
  );
}

function PanelHeader({
  icon,
  subtitle,
  title
}: {
  icon: ReactNode;
  subtitle: string;
  title: string;
}) {
  return (
    <header className="night-video-panel-header">
      <span>{icon}</span>
      <div>
        <h2>{title}</h2>
        <p>{subtitle}</p>
      </div>
    </header>
  );
}

function VideoArtwork({
  compact = false,
  video
}: {
  compact?: boolean;
  video: VideoCompanionVideo;
}) {
  return (
    <span className={clsx("night-video-artwork", compact && "night-video-artwork--compact")} aria-hidden="true">
      <img src={assetUrl(video.imageSrc)} alt="" draggable={false} />
      <span className="night-video-artwork__shade" />
      <Play size={compact ? 18 : 24} />
      <strong>{video.title}</strong>
    </span>
  );
}
